
import * as THREE from "../../libs/three.js/build/three.module.js";
import { EventDispatcher } from "../EventDispatcher.js";
import { Utils } from "../utils.js";
import { YDPolygonVolume, MAX_POLYGON_VERTICES, validateChainAppend, validateChainClose, validateChainMove } from "./YDPolygonVolume.js";

/**
 * PolygonVolumeTool — 异形多边形棱柱包围盒的采点工具。
 *
 * 手势：
 * - startInsertion() 保持当前视角（不切俯视），采点全程导航可用（MeasuringTool 同款：
 *   不锁 inputHandler 也不锁 controls）——拖动旋转/平移、滚轮缩放随时可做；
 * - 用「mousedown→mouseup 位移 ≤ 6px」判定单击：左键单击才加点（拖动旋转松开不算），
 *   右键单击才闭合（右键拖动平移不会误触发闭合）；
 * - 采点会话反馈层：标记球用 MeasuringTool 红球同款方案（Lambert 受光、逐帧恒定
 *   屏幕大小、mouseover 高亮、可拖拽改点位置——drag/drop 监听挂上后 InputHandler 在
 *   mousedown 命中时自动发起拖拽，拖拽期间导航控件不收 mousemove 相机不转），两点起
 *   连轮廓线，挂 viewer.volumeTool.scene（与闭合后的持久标记同一灯光场景，两阶段
 *   外观一致）随会话结束整体移除；
 * - 自交防护（validateChainAppend/Close/Move，YDPolygonVolume.js 纯函数）：新点与
 *   已有边交叉或重合直接拒绝（派发 polygon_point_rejected），闭合前校验闭合边，
 *   拖动标记移入非法位置时忽略该次事件（球停在上一合法位置）；
 * - 左键单击采点：统一拾取入口 Utils.getMouseIntersection（点云 miss 且有高斯时兜底 GS，
 *   utils.js:694）；仍未命中且已有采点时，沿射线取 z=上一有效 Z 的水平面交点兜底——
 *   保证同一水平面采点 Z 一致、不会因点云空洞把棱柱底面拉歪；
 * - Backspace/Delete 撤销上一点；ESC 取消（注意：cancel_insertions 事件只在其他工具
 *   开始插入时派发，不会由 ESC 自动触发，所以 ESC/Backspace 都要自己监听 keydown）；
 * - 右键闭合（≥3 点有效，不足则整体取消）；达到 MAX_POLYGON_VERTICES 后拒绝继续采点。
 *
 * 预览策略：插入一开始就 scene.addVolume（clip=false 不参与裁剪收集），每次采点后
 * volume.setFromWorldPoints() 实时重建棱柱预览（含自动闭合边）；闭合后派发选中让
 * 属性面板接管；取消走 scene.removeVolume（dispose 语义，丢弃预览对象）。
 */
export class PolygonVolumeTool extends EventDispatcher {

	constructor(viewer) {
		super();

		this.viewer = viewer;
		this.renderer = viewer.renderer;
		this.maxVertices = MAX_POLYGON_VERTICES;
		// 是否处于采点过程（防重入：采点中再次点击工具按钮直接忽略）
		this.inserting = false;
	}

	startInsertion(args = {}) {
		const viewer = this.viewer;
		const scene = viewer.scene;
		const domElement = viewer.renderer.domElement;
		const self = this;

		if (this.inserting) {
			console.warn('[PolygonVolumeTool] 已在采点中，忽略重复启动（右键闭合 / ESC 取消当前）');
			return null;
		}
		this.inserting = true;

		// 不自动切俯视：保持用户当前视角采点；导航全程可用，靠位移阈值区分单击与拖动
		// 开始新插入前，取消其他工具进行中的插入（MeasuringTool.js:137 同款联动）
		viewer.dispatchEvent({ type: 'cancel_insertions' });

		const volume = new YDPolygonVolume(args);
		volume.name = args.name || volume.name;

		this.dispatchEvent({ type: 'start_inserting_polygon_volume', volume });

		// 预览即挂载：进 scene.volumes 才会被 VolumeTool 私有 scene 渲染；
		// clip 默认 false，闭合后由调用方/面板开启
		scene.addVolume(volume);

		// —— 采点会话反馈层（挂 volumeTool 私有场景，会话结束整体移除）——
		// 前 2 个点还没有棱柱可显，纯采点毫无反馈；这里把每个已采点画成标记球、
		// 两点起把已采点连成轮廓线；≥3 点后棱柱自带线框，轮廓线撤掉只留标记球。
		// 标记采用 MeasuringTool/Measure 红球同款方案（Measure.js:31/343/MeasuringTool.js:327）：
		// Lambert 材质 + 逐帧 15/projectedRadius 恒定屏幕大小（约 6px 半径），mouseover 高亮。
		// 挂在 volumeTool 私有场景（半球光+平行光+环境光，VolumeTool.js:21-39）：与闭合后的
		// 持久标记球同一灯光环境，两阶段外观完全一致，无需再配相机头灯；
		// 该场景已 registerInteractiveScene（VolumeTool.js:39），标记可拾取可拖拽。
		// 挂 drag/drop/mouseover 监听后 InputHandler 在 mousedown命中时自动发起拖拽
		// （InputHandler.js:318-328）——标记必须可拾取，不能置空 raycast。
		const previewGroup = new THREE.Group();
		const markerGeometry = new THREE.SphereGeometry(0.4, 10, 10);
		const outlineMaterial = new THREE.LineBasicMaterial({
			color: 0x00ffff, transparent: true, opacity: 0.9, depthTest: false
		});
		const markerMeshes = [];
		let outline = null;
		viewer.volumeTool.scene.add(previewGroup);

		// —— 标记拖拽（Measure.js 的 rAF 节流 + miss 兜底同款）——
		let dragRaf = 0;
		let pendingDragEvt = null;
		let lastHitLocation = null;
		const processMarkerDrag = (e) => {
			const camera = scene.getActiveCamera();
			const hit = Utils.getMouseIntersection(e.drag.end, camera, viewer);
			let location = null;
			if (hit) {
				location = hit.location;
			} else if (lastHitLocation) {
				// miss 兜底：用上次命中点的观察平面反投影，保证拖拽连贯
				location = Utils.mouseToPlaneIntersection(
					e.drag.end, camera, viewer.renderer, lastHitLocation);
			}
			if (!location) {
				return;
			}
			const idx = markerMeshes.indexOf(e.drag.object);
			if (idx === -1) {
				return;
			}
			// 拖拽自交防护：移入会造成交叉/重合的位置时忽略本次事件，
			// 标记留在上一个合法位置（lastHitLocation 也不推进，避免兜底平面跟着漂移）
			const verdict = validateChainMove(location, idx, worldPoints);
			if (!verdict.ok) {
				return;
			}
			lastHitLocation = location.clone();
			markerMeshes[idx].position.copy(location);
			worldPoints[idx].copy(location);
			volume.setFromWorldPoints(worldPoints);
			if (worldPoints.length === 2 && outline) {
				outline.geometry.setFromPoints(worldPoints);
			}
		};
		const onMarkerDrag = (e) => {
			// GS WASM raycast 单次数 ms，快速拖拽合并同帧多次 drag 只处理最后一次
			pendingDragEvt = e;
			if (dragRaf) {
				return;
			}
			dragRaf = requestAnimationFrame(() => {
				dragRaf = 0;
				const evt = pendingDragEvt;
				pendingDragEvt = null;
				if (evt) {
					processMarkerDrag(evt);
				}
			});
		};
		const onMarkerDrop = () => {
			if (dragRaf) {
				cancelAnimationFrame(dragRaf);
				dragRaf = 0;
			}
			pendingDragEvt = null;
			lastHitLocation = null;
		};
		const onMarkerOver = (e) => { e.object.material.emissive.setHex(0x888888); };
		const onMarkerLeave = (e) => { e.object.material.emissive.setHex(0x000000); };

		// 每帧：标记恒定屏幕尺寸（MeasuringTool.update 的球体段同款）
		const onViewerUpdate = () => {
			const camera = scene.getActiveCamera();
			const domElement = viewer.renderer.domElement;
			const size = viewer.renderer.getSize(new THREE.Vector2());
			for (const marker of markerMeshes) {
				const distance = camera.position.distanceTo(marker.getWorldPosition(new THREE.Vector3()));
				const pr = Utils.projectedRadius(1, camera, distance, domElement.clientWidth, domElement.clientHeight);
				marker.scale.setScalar(15 / pr);
			}
		};
		viewer.addEventListener('update', onViewerUpdate);

		const rebuildPreview = () => {
			// 清空：标记球共享 markerGeometry 不能 dispose，但材质是每球独立的
			// （Measure.js:363 同款 createSphereMaterial 每球新建），清掉时一并释放
			for (const child of [...previewGroup.children]) {
				previewGroup.remove(child);
				if (child.material && child !== outline) {
					child.material.dispose();
				}
			}
			if (outline) {
				outline.geometry.dispose();
				outline = null;
			}
			markerMeshes.length = 0;

			const camera = scene.getActiveCamera();
			for (const wp of worldPoints) {
				const marker = new THREE.Mesh(markerGeometry, new THREE.MeshLambertMaterial({ color: 0x00ffff }));
				marker.material.depthTest = false;
				marker.material.depthWrite = false;
				marker.position.copy(wp);
				marker.renderOrder = 10;
				// 初始尺寸给个近似值，下一帧 onViewerUpdate 校正为恒定屏幕大小
				marker.scale.setScalar(Math.max(camera.position.distanceTo(wp) * 0.01, 1e-6));
				marker.addEventListener('drag', onMarkerDrag);
				marker.addEventListener('drop', onMarkerDrop);
				marker.addEventListener('mouseover', onMarkerOver);
				marker.addEventListener('mouseleave', onMarkerLeave);
				previewGroup.add(marker);
				markerMeshes.push(marker);
			}
			if (worldPoints.length === 2) {
				outline = new THREE.Line(
					new THREE.BufferGeometry().setFromPoints(worldPoints),
					outlineMaterial);
				outline.renderOrder = 10;
				outline.raycast = () => {};
				previewGroup.add(outline);
			}
		};

		const worldPoints = [];
		let lastValidZ = null;

		// 沿射线取 z=zLevel 水平面上的交点（采点 miss 时的 Z 兜底）
		const rayAtZ = (mouse, camera, zLevel) => {
			const nmouse = {
				x: (mouse.x / domElement.clientWidth) * 2 - 1,
				y: -(mouse.y / domElement.clientHeight) * 2 + 1
			};
			const raycaster = new THREE.Raycaster();
			raycaster.setFromCamera(nmouse, camera);
			const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), -zLevel);
			const out = new THREE.Vector3();
			return raycaster.ray.intersectPlane(plane, out) ? out : null;
		};

		const finish = (closed) => {
			// 闭合边自交校验：交叉则拒绝闭合，保持采点态（拖动标记调整后再闭合）
			if (closed) {
				const closeVerdict = validateChainClose(worldPoints);
				if (!closeVerdict.ok) {
					console.warn('[PolygonVolumeTool] 闭合边与已有边交叉，无法闭合（拖动标记调整点位后重试，或 ESC 取消）');
					self.dispatchEvent({ type: 'polygon_point_rejected', volume: volume, reason: 'closing_cross' });
					return;
				}
			}

			domElement.removeEventListener('mouseup', onMouseUp, true);
			domElement.removeEventListener('mousedown', onDown, true);
			domElement.removeEventListener('contextmenu', onContextMenu, true);
			window.removeEventListener('keydown', onKeyDown, true);
			viewer.removeEventListener('cancel_insertions', onCancel);
			viewer.removeEventListener('update', onViewerUpdate);
			// 采点会话反馈层随会话结束整体移除（每球独立材质一并释放）
			viewer.volumeTool.scene.remove(previewGroup);
			for (const child of previewGroup.children) {
				if (child.material) {
					child.material.dispose();
				}
			}
			markerGeometry.dispose();
			outlineMaterial.dispose();
			if (outline) {
				outline.geometry.dispose();
				outline = null;
			}
			self._finish = null;
			self.inserting = false;

			if (closed && worldPoints.length >= 3) {
				self.dispatchEvent({
					type: 'finished_inserting_polygon_volume',
					volume: volume,
					points: worldPoints.length
				});
				viewer.inputHandler.setSelection([volume]);
				// 本库没有「3D 选中→面板」的直连链路：propertiesPanel.set 只由 jstree
				// select_node 驱动（sidebar.js:456），所以这里选中侧栏树节点让面板接管
				// （与 sidebar 的 VOLUME 工具按钮回调整套动作一致）。无侧栏的嵌入式用法静默跳过。
				try {
					let measurementsRoot = $("#jstree_scene").jstree().get_json("measurements");
					let jsonNode = measurementsRoot.children.find(child => child.data.uuid === volume.uuid);
					if (jsonNode) {
						$.jstree.reference(jsonNode.id).deselect_all();
						$.jstree.reference(jsonNode.id).select_node(jsonNode.id);
					}
				} catch (e) { /* ignore */ }
			} else {
				// 预览半途取消：removeVolume 是 dispose 语义，直接丢弃
				scene.removeVolume(volume);
				self.dispatchEvent({ type: 'cancelled_polygon_insertion' });
			}
		};

		const onCancel = () => {
			finish(false);
		};

		// 供 stopInsertion() 外部结束采点用（finish 是闭包函数，挂到实例上）
		self._finish = finish;

		const onContextMenu = (e) => {
			// 右键的 contextmenu 已被 InputHandler 全局 preventDefault（InputHandler.js:46），
			// 这里只需吞掉右键 mouseup 前的多余事件，防止闭合瞬间弹出系统菜单的边缘情况
			e.preventDefault();
		};

		const onKeyDown = (e) => {
			if (e.key === 'Backspace' || e.key === 'Delete') {
				e.preventDefault();
				if (worldPoints.length > 0) {
					worldPoints.pop();
					if (worldPoints.length === 0) {
						lastValidZ = null;
					}
					volume.setFromWorldPoints(worldPoints);
					rebuildPreview();
					self.dispatchEvent({
						type: 'polygon_point_removed',
						volume: volume,
						points: worldPoints.length
					});
				}
			} else if (e.key === 'Escape') {
				finish(false);
			}
		};

		// 单击 vs 拖动判定：mousedown 记录起点，mouseup 位移 ≤ 6px 才算单击。
		// 没有对应 mousedown（按下点不在画布、拖进画布内松开）一律不算单击。
		// 按下点落在标记球上时 skip=true：那一下是「选中标记准备拖拽」，不能当作
		// 采点单击（否则在标记上原地点一下会多出一个同位置的新点）
		const DRAG_THRESHOLD = 6;
		let downInfo = null;
		const onDown = (e) => {
			let skip = false;
			if (markerMeshes.length > 0) {
				const rect = domElement.getBoundingClientRect();
				const nmouse = new THREE.Vector2(
					((e.clientX - rect.left) / rect.width) * 2 - 1,
					-((e.clientY - rect.top) / rect.height) * 2 + 1
				);
				const raycaster = new THREE.Raycaster();
				raycaster.setFromCamera(nmouse, scene.getActiveCamera());
				skip = raycaster.intersectObjects(markerMeshes, false).length > 0;
			}
			downInfo = { x: e.clientX, y: e.clientY, button: e.button, skip };
		};
		const isClick = (e) => {
			if (!downInfo || downInfo.button !== e.button) return false;
			let dx = e.clientX - downInfo.x;
			let dy = e.clientY - downInfo.y;
			downInfo = null;
			return dx * dx + dy * dy <= DRAG_THRESHOLD * DRAG_THRESHOLD;
		};

		const onMouseUp = (e) => {
			// 拖动旋转/平移松开不落点、不闭合；标记球上按下的那一下留给拖拽
			const info = downInfo;
			if (!isClick(e) || (info && info.skip)) {
				return;
			}

			if (e.button === THREE.MOUSE.LEFT) {
				if (worldPoints.length >= self.maxVertices) {
					console.warn(`[PolygonVolumeTool] 已达顶点上限 ${self.maxVertices}，右键闭合或 Backspace 删点`);
					return;
				}

				const mouse = { x: e.offsetX, y: e.offsetY };
				const camera = scene.getActiveCamera();
				// 统一拾取入口：按 sceneShowModel 路由 PC/GS（utils.js:694）
				const hit = Utils.getMouseIntersection(mouse, camera, viewer);

				let point = null;
				if (hit) {
					point = hit.location.clone();
					lastValidZ = point.z;
				} else if (worldPoints.length > 0 && lastValidZ !== null) {
					// miss 兜底：取 z=上一有效 Z 的水平面交点，保持棱柱底面平整
					point = rayAtZ(mouse, camera, lastValidZ);
				}

				if (!point) {
					return; // 第一点必须命中数据，否则忽略本次点击
				}

				// 自交防护：新边与已有边交叉、或与已有顶点重合的点直接拒绝——
				// 交叉后的 even-odd 空间不再是合法多边形
				const verdict = validateChainAppend(point, worldPoints);
				if (!verdict.ok) {
					console.warn(`[PolygonVolumeTool] 新点被拒绝（${verdict.reason === 'cross' ? '与已有边交叉' : '与已有顶点重合'}）`);
					self.dispatchEvent({ type: 'polygon_point_rejected', volume: volume, reason: verdict.reason });
					return;
				}

				worldPoints.push(point);
				volume.setFromWorldPoints(worldPoints);
				rebuildPreview();
				self.dispatchEvent({
					type: 'polygon_point_added',
					volume: volume,
					points: worldPoints.length
				});
			} else if (e.button === THREE.MOUSE.RIGHT) {
				if (worldPoints.length >= 3) {
					finish(true);
				} else {
					// 不足 3 点无法构成多边形，整体取消
					finish(false);
				}
			}
		};

		// capture 监听（ClippingTool.js:160 同款）；keydown 挂 window capture，
		// 避免 inputHandler 的快捷键抢先处理。导航不锁：拖动旋转/滚轮缩放全程可用
		viewer.addEventListener('cancel_insertions', onCancel);
		domElement.addEventListener('mousedown', onDown, true);
		domElement.addEventListener('mouseup', onMouseUp, true);
		domElement.addEventListener('contextmenu', onContextMenu, true);
		window.addEventListener('keydown', onKeyDown, true);

		return volume;
	}

	/**
	 * 外部主动结束采点（侧栏工具按钮再次点击 / 测试页「结束采点」按钮）。
	 * ≥3 点闭合成交（与右键同效），不足 3 点整体取消；非采点状态调用是空操作。
	 */
	stopInsertion() {
		if (!this.inserting || !this._finish) {
			return;
		}
		this._finish(true);
	}

};
